import type { QueryClient } from '@tanstack/react-query';

export const CART_QUERY_KEY = ['/api/cart'] as const;

// Numbers each add so a slow response can't overwrite the cart from a later one
let lastAddId = 0;

/** Call when an add-to-cart request starts; pass the result to applyCartResponse. */
export function beginCartAdd(): number {
  lastAddId += 1;
  return lastAddId;
}

/**
 * Put the cart returned by POST /api/cart straight into the cache, so the badge and the
 * cart page show the new item as soon as the add finishes rather than after a refetch.
 * Any cart fetch already in flight is cancelled first: it started before the add, and if it
 * landed afterwards it would overwrite the new cart with the old one.
 */
export async function applyCartResponse(queryClient: QueryClient, data: unknown, addId: number) {
  await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });
  // A later add has started: this response may predate it, so ask the server instead
  if (addId !== lastAddId) {
    queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    return;
  }
  if (data && Array.isArray((data as { items?: unknown }).items)) {
    queryClient.setQueryData(CART_QUERY_KEY, data);
  } else {
    // The server added the item but couldn't format the cart; fetch it instead
    queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
  }
}
